import * as React from 'react';
import classNames from 'classnames';

import AdditionalFilter from '~/Components/AdditionalFilter/AdditionalFilter';
import Filter from '~/Components/Filter/Filter';
import Footer from '~/Components/Footer/Footer';
import ProgressBar from '~/Components/ProgressBar/ProgressBar';
import Search from '~/Components/Search/Search';
import { playButtonClickSound, playButtonDialogClickSound } from '~/helpers/audioApi';
import { t } from '~/helpers/localization';
import { throttle } from '~/helpers/throttle';
import { isInGameBrowser } from '~/helpers/utils';
import useScroll from '~/hooks/useScroll';
import { ICategories, Item as IItem } from '~/interfaces';
import { onClickByHeaderTitle, parseCategoryTitle } from '~/Layouts/Category/CategoryPreTitle';

import VirtualScroller, { ROW_HEIGHT } from './VirtualScroller/VirtualScroller';

import styles from './CategoryLayout.scss';

interface ICategoryLayout {
    availableAwardsCount: number;
    totalCount: number;
    filteredTotalCount?: number;
    uniqueAwardsCount: number;
    items: IItem[];
    inventoryItems: any;
    category: ICategories;
}

const CategoryLayout = (props: ICategoryLayout) => {
    const categoryBodyRef = React.useRef<HTMLDivElement>(null);
    const contentRef = React.useRef<HTMLDivElement>(null);

    const shadowsRef = React.useRef<HTMLDivElement>(null);
    const buttonToTop = React.useRef<HTMLDivElement>(null);

    const [firstVisibleRow, setFirstVisibleRow] = React.useState(0);

    const [containerWidth, setContainerWidth] = React.useState(0);
    const [containerHeight, setContainerHeight] = React.useState(0);

    React.useLayoutEffect(() => {
        const updateSize = () => {
            if (!categoryBodyRef.current || !contentRef.current) {
                return;
            }

            const style = window.getComputedStyle(contentRef.current);
            const padding = parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);

            setContainerWidth(contentRef.current.clientWidth - padding);
            setContainerHeight(categoryBodyRef.current.clientHeight);
        };

        const throttledUpdateSize = throttle(updateSize, 100);

        updateSize();

        window.addEventListener('resize', throttledUpdateSize);

        return () => {
            window.removeEventListener('resize', throttledUpdateSize);
        };
    }, []);

    const onScroll = (event: React.UIEvent) => {
        if (!shadowsRef.current || !buttonToTop.current) {
            return;
        }

        const target = event.target as HTMLDivElement;

        setFirstVisibleRow(Math.floor(target.scrollTop / ROW_HEIGHT));

        if (target.scrollTop > 10) {
            shadowsRef.current.classList.add(styles.visibleShadows);
        } else {
            shadowsRef.current.classList.remove(styles.visibleShadows);
        }

        if (target.scrollTop > 100) {
            buttonToTop.current.classList.add(styles.visibleScrollTop);
        } else {
            buttonToTop.current.classList.remove(styles.visibleScrollTop);
        }
    };

    useScroll(
        categoryBodyRef,
        () => {
            categoryBodyRef.current?.classList.add(styles.cursorDisabled);
        },
        () => {
            categoryBodyRef.current?.classList.remove(styles.cursorDisabled);
        },
    );

    const scrollToTop = () => {
        playButtonClickSound();

        if (categoryBodyRef.current) {
            categoryBodyRef.current.scrollTop = 0;
        }
    };

    return (
        <React.Fragment>
            <div className={styles.categoryHeader}>
                <div className={styles.categoryHeaderTitleWrapper}>
                    <div
                        className={styles.categoryHeaderTitle}
                        onClick={(event: React.MouseEvent) => {
                            playButtonDialogClickSound();

                            onClickByHeaderTitle(event, props.category);
                        }}
                        dangerouslySetInnerHTML={{
                            __html: parseCategoryTitle(props.category),
                        }}
                    />
                </div>

                <ProgressBar category={props.category} availableAwardsCount={props.availableAwardsCount} totalCount={props.totalCount} uniqueAwardsCount={props.uniqueAwardsCount} />

                <div className={styles.filterWrap}>
                    <Filter category={props.category} availableCount={props.availableAwardsCount} filteredAvailableCount={props.filteredTotalCount} filteredItems={props.items} />

                    <div className={styles.filterBaseLeftPanel}>
                        <Search category={props.category} onKeyDown={null} resetSearch={null} />

                        <AdditionalFilter category={props.category} />
                    </div>
                </div>

                <div ref={shadowsRef} className={classNames(styles.overflowShadows, styles.categoryBodyShadows)} />
            </div>

            <div
                className={classNames(styles.categoryBody, {
                    [styles.withFooter]: !isInGameBrowser,
                })}
                onScroll={onScroll}
                ref={categoryBodyRef}
            >
                <div className={styles.categoryBodyWrapper}>
                    <div className={styles.categoryBodyContent} ref={contentRef}>
                        {props.items.length ? (
                            <VirtualScroller items={props.items} category={props.category} containerWidth={containerWidth} containerHeight={containerHeight} firstVisibleRow={firstVisibleRow} />
                        ) : (
                            <div className={styles.filterNotFound}>{t('Ничего не найдено')}</div>
                        )}
                    </div>
                </div>

                {!isInGameBrowser && <Footer />}
            </div>

            <div className={styles.categoryWrap}>
                <div className={styles.categoryWrapper}>
                    <div className={styles.categoryWrapperContent}>
                        <div className={styles.buttonToTop} ref={buttonToTop} onClick={scrollToTop} />
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
};

export default CategoryLayout;
